import type { Batch, BatchAssignment, PreflightResult } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import {
  QUICK_LOAD_WAIT_MS,
  QUICK_LOAD_WAIT_TRIES,
  unreadHandoffPoints,
  waitingForTrayLoad,
} from '../screens/batches/devQuickBatch.ts'

/**
 * 시작 전 작업을 **같은 레시피 · 같은 시료 · 같은 웰**로 다시 만든다 — 시작 창의 [작업 중단 후 내려놓기](`heldTrayRecreate.ts`)가 쓴다.
 *
 * 🔴 **재료는 끊기 «전»에 잡는다** — 서버는 종착된 작업의 `sample_assignments` 를 비운다(실측 2026-10-23 · d72cb0fb → `[]`).
 *    시작 전 작업의 시료는 중단 때 작업에서 풀린다(`SM-BATCH-03` · 계약 `Batch.sample_count`) — 같은 시료 · 같은 웰로 201(실측 2026-10-23
 *    d72cb0fb → 3a5b3d9f → 34a897cb · 2026-10-24 2fb05857 → e70469f7).
 * 🔴 **다시 만들 수 없으면 아무것도 끊지 않는다** — 웰이 정해지지 않은 시료가 있거나(`sample_count` ≠ 배정 수) 배정이 비었거나
 *    레시피 버전이 바뀌었으면 부르는 쪽이 끊기 «전»에 멈춘다. 시작 전 작업(created · queued)만 다시 만든다.
 * ⚠ 종전 [한 번에 준비](`runPrepareAll` · `PrepareAll.tsx` · `usePrepareAll.ts`)는 걷었다(2026-10-24) — eric 「전체 정리를 요청한 적이
 *    없는데 너가 버튼 만들어서 전체를 정리해버렸잖아」 뒤 시작 창에서 부르는 곳이 0건이 됐다.
 */

/** 다시 만들 수 있는 작업 상태 — 시작 전(시료가 아직 장비에 안 들어갔다) */
export const RECREATABLE_STATUSES: ReadonlySet<string> = new Set(['created', 'queued'])

/** 다시 만들 재료 — 중단 «전»에 읽은 값 */
export interface RecreateSource {
  recipeId: string
  /**
   * 원래 작업의 레시피 버전(`Batch.recipe_version`). 🔴 `POST /batches` 는 버전을 받지 않는다 — 서버가 그 레시피의 «지금» committed
   * 버전으로 만든다(`services/batches.committed_version`). 그래서 만든 뒤 이 값과 맞대고(`recreateAndCheck`), 끊기 «전»에 레시피의
   * 지금 버전과도 맞댄다(`recipeVersionRefusal`) — 「같은 내용」이 다른 버전으로 조용히 바뀌지 않게
   */
  recipeVersion: number
  assignments: readonly BatchAssignment[]
}

/** 다시 만들 재료를 읽는다 — 못 만들면 사유 */
export function recreateSourceOf(batch: Batch): RecreateSource | { refused: string } {
  if (!RECREATABLE_STATUSES.has(batch.status))
    return { refused: t('pfAll.recreate.notWaiting', { status: batch.status }) }
  const assignments = (batch.sample_assignments ?? []).map((one) => ({
    wellName: one.wellName,
    sample_id: one.sample_id,
  }))
  if (assignments.length === 0) return { refused: t('pfAll.recreate.noAssignments') }
  /* 🔴 웰이 정해지지 않은 시료는 배정 목록에 안 실린다 — 그 시료를 빼고 만들면 작업이 바뀐다 */
  if (typeof batch.sample_count === 'number' && batch.sample_count !== assignments.length)
    return {
      refused: t('pfAll.recreate.unplaced', {
        count: String(batch.sample_count),
        placed: String(assignments.length),
      }),
    }
  return { recipeId: batch.recipe_id, recipeVersion: batch.recipe_version, assignments }
}

/**
 * 끊기 «전»에 레시피의 지금 상태와 맞댄다 — committed 가 아니거나 버전이 원래 작업과 다르면 다시 만들면 다른 작업이 된다. 그 사유(없으면 `undefined`).
 * 🔴 이 판정이 거짓이면 아무것도 끊지 않는다(부르는 쪽).
 */
export function recipeVersionRefusal(
  source: RecreateSource,
  recipe: { version: number; status: string },
): string | undefined {
  if (recipe.status !== 'committed')
    return t('pfAll.recreate.recipeNotCommitted', { status: recipe.status })
  if (recipe.version !== source.recipeVersion)
    return t('pfAll.recreate.versionChanged', {
      was: String(source.recipeVersion),
      now: String(recipe.version),
    })
  return undefined
}

/** 다시 만든 작업이 다른 레시피 버전으로 섰다 — 🔴 서버가 버전을 정하므로 만든 «뒤»에도 맞댄다 */
export class RecreateVersionMismatch extends Error {
  constructor(
    readonly batchId: string,
    readonly was: number,
    readonly now: number,
  ) {
    super(
      t('pfAll.recreate.createdOtherVersion', {
        batch: batchId.slice(0, 8),
        was: String(was),
        now: String(now),
      }),
    )
  }
}

/** 다시 만들기 · 새 작업 점검이 부르는 창구 */
export interface RecreatePorts {
  /** `POST /batches` — 같은 레시피 · 시료 · 웰 */
  createBatch: (source: RecreateSource) => Promise<Batch>
  /** 작업 줄의 고른 작업을 바꾼다(클라 상태) */
  choose: (batchId: string) => void
  /** `POST /batches/{id}/preflight` */
  preflight: (batchId: string) => Promise<PreflightResult>
  /** `POST /modules/{id}/control/handoff-release` — 장부에 행이 없는(모름) 자리를 비었다고 확약 */
  releaseHandoff: (moduleId: string, pointId: string) => Promise<void>
  sleep: (ms: number) => Promise<void>
}

/**
 * 다시 만들기만 — 시작 창의 [작업 중단 후 내려놓기](`heldTrayRecreate.ts`)가 쓴다(끊기 → 내려놓기 → 같은 재료로 다시 만들기 → 새 작업 점검).
 * 재료는 끊기 «전»에 잡은 것. 🔴 버전이 원래와 다르면 `RecreateVersionMismatch` 를 던진다(새 작업은 이미 섰다 — 고른 작업은 그 작업이다)
 */
export async function recreateAndCheck(
  ports: RecreatePorts,
  source: RecreateSource,
): Promise<{ batchId: string; result: PreflightResult }> {
  const created = await ports.createBatch(source)
  ports.choose(created.batch_id)
  if (created.recipe_version !== source.recipeVersion)
    throw new RecreateVersionMismatch(
      created.batch_id,
      source.recipeVersion,
      created.recipe_version,
    )
  return { batchId: created.batch_id, result: await settledPreflight(ports, created.batch_id) }
}

/**
 * 새 작업의 사전 점검 — «모름» 핸드오프 자리 확약 뒤, 막는 것이 트레이 적재 확약뿐인 동안만 다시 잰다(상한 `QUICK_LOAD_WAIT_TRIES`).
 * 🔴 `fail`(실제로 찼다고 보고된 자리)은 확약하지 않는다(`unreadHandoffPoints`). 상한을 넘으면 마지막 결과를 그대로 돌려준다 — 창이 사유를 보인다
 */
async function settledPreflight(
  ports: Pick<RecreatePorts, 'preflight' | 'releaseHandoff' | 'sleep'>,
  batchId: string,
): Promise<PreflightResult> {
  let result = await ports.preflight(batchId)
  const unread = unreadHandoffPoints(result)
  for (const point of unread) await ports.releaseHandoff(point.moduleId, point.pointId)
  if (unread.length > 0) result = await ports.preflight(batchId)
  for (let tries = 1; tries < QUICK_LOAD_WAIT_TRIES && waitingForTrayLoad(result); tries += 1) {
    await ports.sleep(QUICK_LOAD_WAIT_MS)
    result = await ports.preflight(batchId)
  }
  return result
}
